import { useState, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { BsTelephone, BsCheckCircleFill } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Select from '#globalComponents/Forms/Select';
import Switch from '#globalComponents/Forms/Switch';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useErrorStore } from '#lib/stores/errorStore';
import { useOrgMembersBasic } from '#orgFeature/models/org/queries';

import {
	startAircallOAuth,
	updateAircallSettings,
	disconnectIntegration,
	getAircallUsers,
	updateAircallUserMappings,
} from '../../../../api/crmApi';
import { integrationsKeys } from '../../../../models/integrations/queries';

const AircallConfigModal = ({ isOpen, onClose, integration }) => {
	const qc = useQueryClient();
	const showError = useErrorStore((s) => s.showError);
	const showSuccess = useErrorStore((s) => s.showSuccess);

	const isConnected = integration?.isConnected ?? false;

	// Org members
	const { data: membersData } = useOrgMembersBasic();
	const members = membersData?.members ?? [];

	// Settings state
	const [syncLoading, setSyncLoading] = useState(false);
	const [disconnectOpen, setDisconnectOpen] = useState(false);
	const [disconnecting, setDisconnecting] = useState(false);

	// User mapping state
	const [aircallUsers, setAircallUsers] = useState([]);
	const [aircallUsersLoading, setAircallUsersLoading] = useState(false);
	const [mappings, setMappings] = useState([]);
	const [mappingSaving, setMappingSaving] = useState(false);

	// Load Aircall users when modal opens and is connected
	useEffect(() => {
		if (!isOpen || !isConnected) return;
		setAircallUsersLoading(true);
		getAircallUsers()
			.then((res) => setAircallUsers(res.users ?? []))
			.catch(() => {})
			.finally(() => setAircallUsersLoading(false));
	}, [isOpen, isConnected]);

	// Initialize mappings from integration data
	useEffect(() => {
		if (!isOpen) return;
		setMappings(integration?.userMappings ?? []);
	}, [isOpen, integration?.userMappings]);

	// Connect state
	const [connecting, setConnecting] = useState(false);

	// Connect via OAuth

	const handleConnect = async () => {
		setConnecting(true);
		try {
			const { url } = await startAircallOAuth();
			// Redirect browser to Aircall's OAuth consent screen
			window.location.href = url;
		} catch (err) {
			showError({ error: err, title: 'OAuth-Verbindung konnte nicht gestartet werden' });
			setConnecting(false);
		}
	};

	// Settings

	const handleSyncToggle = async () => {
		const next = !integration?.callSyncEnabled;
		setSyncLoading(true);
		try {
			await updateAircallSettings({ callSyncEnabled: next });
			qc.invalidateQueries({ queryKey: integrationsKeys.all });
		} catch (err) {
			showError({ error: err, title: 'Einstellung konnte nicht gespeichert werden' });
		} finally {
			setSyncLoading(false);
		}
	};

	// User Mapping

	const getMappingForMember = (crmUserId) => mappings.find((m) => m.crmUserId === crmUserId);

	const handleMappingChange = (crmUserId, aircallUserId) => {
		const aircallUser = aircallUsers.find((u) => String(u.id) === String(aircallUserId));
		setMappings((prev) => {
			const filtered = prev.filter((m) => m.crmUserId !== crmUserId);
			if (aircallUserId) {
				filtered.push({
					crmUserId,
					aircallUserId: Number(aircallUserId),
					aircallUserName: aircallUser
						? `${aircallUser.first_name ?? ''} ${aircallUser.last_name ?? ''}`.trim()
						: '',
				});
			}
			return filtered;
		});
	};

	const handleSaveMappings = async () => {
		setMappingSaving(true);
		try {
			await updateAircallUserMappings(mappings);
			qc.invalidateQueries({ queryKey: integrationsKeys.all });
			showSuccess({ title: 'Benutzerzuordnung gespeichert', autoClose: true });
		} catch (err) {
			showError({ error: err, title: 'Zuordnung konnte nicht gespeichert werden' });
		} finally {
			setMappingSaving(false);
		}
	};

	// Disconnect

	const handleDisconnect = async () => {
		setDisconnecting(true);
		try {
			await disconnectIntegration('phone_aircall');
			qc.invalidateQueries({ queryKey: integrationsKeys.all });
			setDisconnectOpen(false);
		} catch (err) {
			showError({ error: err, title: 'Verbindung konnte nicht getrennt werden' });
		} finally {
			setDisconnecting(false);
		}
	};

	return (
		<>
			<Modal isOpen={isOpen} onClose={onClose} size="m">
				<Panel.Header icon={BsTelephone} onClose={onClose} subtitle="Telefonie-Integration">
					Aircall
				</Panel.Header>
				<Panel.Body>
					{!isConnected ? (
						<div className="space-y-4">
							<div className="bg-primary-100 border border-primary-200 rounded-card p-4">
								<p className="text-sm text-primary-700">
									Verbinden Sie Ihr Aircall-Konto per OAuth. Sie werden zu Aircall weitergeleitet, um
									die Verbindung zu autorisieren.
								</p>
							</div>

							<Button
								variant="solid"
								size="md"
								onClick={handleConnect}
								disabled={connecting}
								loading={connecting}
								fullWidth
							>
								{connecting ? 'Wird verbunden…' : 'Mit Aircall verbinden'}
							</Button>
						</div>
					) : (
						<div className="space-y-5">
							<div className="flex items-center gap-2 bg-success-200 border border-success-500/40 rounded-card px-4 py-3">
								<BsCheckCircleFill className="w-4 h-4 text-success-700" />
								<span className="text-sm font-medium text-success-700">
									Verbunden
									{integration?.providerCompanyName ? ` mit ${integration.providerCompanyName}` : ''}
								</span>
							</div>

							<Card>
								<div className="flex items-center justify-between">
									<div>
										<h3 className="text-sm font-medium text-text">Anruf-Synchronisation</h3>
										<p className="text-xs text-bgBase-800 mt-0.5">
											Anrufe automatisch als Aktivitäten synchronisieren
										</p>
									</div>
									<Switch
										checked={Boolean(integration?.callSyncEnabled)}
										onChange={handleSyncToggle}
										disabled={syncLoading}
									/>
								</div>
							</Card>

							<Card>
								<h3 className="text-sm font-medium text-text mb-1">Benutzerzuordnung</h3>
								<p className="text-xs text-bgBase-800 mb-3">
									Ordnen Sie CRM-Benutzer ihren Aircall-Konten zu. Ohne Zuordnung wird automatisch per
									E-Mail-Adresse gematcht.
								</p>

								{aircallUsersLoading ? (
									<div className="flex items-center gap-2 py-3 text-xs text-bgBase-800">
										<div className="animate-spin h-4 w-4 border-2 border-bgBase-600 border-t-transparent rounded-full" />
										Aircall-Benutzer werden geladen…
									</div>
								) : (
									<div className="space-y-2">
										{members.map((member) => {
											const current = getMappingForMember(member.userId);
											return (
												<div key={member.userId} className="flex items-center gap-3">
													<span
														className="text-xs text-text font-medium w-1/3 truncate"
														title={member.fullName ?? member.emailAddress}
													>
														{member.fullName || member.emailAddress}
													</span>
													<Select
														value={current?.aircallUserId ?? ''}
														onChange={(e) =>
															handleMappingChange(member.userId, e.target.value || null)
														}
														className="flex-1"
													>
														<option value="">Automatisch (E-Mail)</option>
														{aircallUsers.map((au) => (
															<option key={au.id} value={au.id}>
																{`${au.first_name ?? ''} ${au.last_name ?? ''}`.trim() ||
																	au.email}
															</option>
														))}
													</Select>
												</div>
											);
										})}
									</div>
								)}

								<div className="mt-3 flex justify-end">
									<Button
										variant="solid"
										size="sm"
										onClick={handleSaveMappings}
										disabled={mappingSaving || aircallUsersLoading}
										loading={mappingSaving}
									>
										Zuordnung speichern
									</Button>
								</div>
							</Card>

							<div className="pt-2 border-t border-bgBase-400">
								<Button variant="danger" size="sm" onClick={() => setDisconnectOpen(true)}>
									Verbindung trennen
								</Button>
							</div>
						</div>
					)}
				</Panel.Body>
			</Modal>

			<ConfirmModal
				isOpen={disconnectOpen}
				onClose={() => setDisconnectOpen(false)}
				onConfirm={handleDisconnect}
				title="Aircall trennen"
				message="Möchten Sie die Aircall-Verbindung wirklich trennen? Die Synchronisation wird gestoppt. Bestehende Aktivitäten bleiben erhalten."
				confirmText="Trennen"
				isLoading={disconnecting}
			/>
		</>
	);
};

AircallConfigModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	integration: PropTypes.object,
};

export default AircallConfigModal;
